import React, { useCallback, useEffect, useRef, useState } from "react";
import type { HandDetector } from "@tensorflow-models/hand-pose-detection";
import type { ObjectDetection } from "@tensorflow-models/coco-ssd";
import type { PeerMessage } from "@/types/peerDetection";

interface MobileInterviewStreamProps {
  localStream: MediaStream | null;
  selectedCamera: string;
  handDetector: HandDetector | null;
  objectDetector: ObjectDetection | null;
  detectionStatusMessage: string;
  isDetectionPreparing: boolean;
  detectionError: Error | null;
  onCompositeStreamReady?: (stream: MediaStream | null) => void;
  onDisconnect?: () => void;
  sendPeerMessage?: (message: PeerMessage) => boolean;
  isPeerConnected?: boolean;
}

/*
  ------------------------------------------------------
  Simplified mobile stream: render the local camera full-screen
  ------------------------------------------------------
*/
const MobileInterviewStream: React.FC<MobileInterviewStreamProps> = ({
  localStream,
  selectedCamera,
  onCompositeStreamReady,
  onDisconnect,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const handleTestDisconnect = useCallback(() => {
    if (!onDisconnect) return;
    onDisconnect();
  }, [onDisconnect]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    if (localStream) {
      videoRef.current.srcObject = localStream;
      const handleLoadedMetadata = () => setIsVideoReady(true);
      videoRef.current.onloadedmetadata = handleLoadedMetadata;
      void videoRef.current.play().catch(() => undefined);
      onCompositeStreamReady?.(localStream);
    } else {
      videoRef.current.srcObject = null;
      setIsVideoReady(false);
      onCompositeStreamReady?.(null);
    }
  }, [localStream, onCompositeStreamReady]);

  return (
    <div className="fixed inset-0 h-full w-full bg-black">
      <div className="relative h-full w-full">
        {localStream ? (
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-contain"
            playsInline
            muted
            autoPlay
            style={{ transform: "scaleX(-1)" }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-white text-sm">
            Preparing camera feed from {selectedCamera || "selected device"}…
          </div>
        )}

        {!isVideoReady && localStream && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 text-white">
            <div className="mb-3 h-10 w-10 animate-spin rounded-full border-b-2 border-white" />
            <p className="text-sm">Starting camera…</p>
          </div>
        )}

    
      </div>
    </div>
  );
};

export default MobileInterviewStream;
